import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { itineraryApi } from '../services/api';
import { Loader2, MapPin, Calendar, Camera, Star, Check, Clock, Plane, Bus, Train, Car, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const TransportIcon = ({ type }) => {
  const t = (type || '').toLowerCase();
  if (t.includes('flight') || t.includes('plane')) return <Plane className="w-3 h-3" />;
  if (t.includes('bus')) return <Bus className="w-3 h-3" />;
  if (t.includes('train')) return <Train className="w-3 h-3" />;
  return <Car className="w-3 h-3" />;
};

export default function SharedTimelinePage() {
  const { shareId } = useParams();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadTimeline = useCallback(async () => {
    try {
      const res = await itineraryApi.getSharedTimeline(shareId);
      setTimeline(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTimeline();
  }, [loadTimeline]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  if (error || !timeline) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70">
        <Header />
        <div className="pt-24 text-center text-white/60">
          <p className="text-xl">Timeline not found or has been removed.</p>
        </div>
      </div>
    );
  }

  const { trip_input, days, summary, review, author_name } = timeline;
  const city = trip_input?.destination_cities?.length > 1
    ? trip_input.destination_cities.join(' > ')
    : trip_input?.destination_city || 'Unknown';

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="shared-timeline-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-[#0d4a6b] to-sky/80" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl animate-pulse" />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-4 sm:px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <p className="text-gold text-sm tracking-[0.2em] uppercase font-semibold mb-2">Trip Story by {author_name}</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-white drop-shadow-lg mb-2">{city}</h1>
            <p className="text-white/60 text-lg">{trip_input?.destination_country || ''}</p>
            {trip_input?.start_date && (
              <p className="text-white/50 mt-2 text-sm">{formatDate(trip_input.start_date)} - {formatDate(trip_input.end_date)}</p>
            )}
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <Calendar className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{summary?.total_days || 0} days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <MapPin className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{summary?.cities_visited?.length || 0} cities</p>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <Camera className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{summary?.total_photos || 0} photos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <DollarSign className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-xl font-bold text-white">${summary?.total_spent?.toFixed(0) || '0'}</p>
              <p className="text-white/40 text-xs">Spent</p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold via-sky/50 to-transparent" />

            {days.map((day, idx) => (
              <motion.div
                key={day.day_number}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * idx }}
                className="relative pl-16 sm:pl-20 mb-10"
              >
                <div className="absolute left-3 sm:left-5 top-0 w-7 h-7 rounded-full bg-gold border-4 border-ocean flex items-center justify-center z-10">
                  <span className="text-ocean text-[10px] font-bold">{day.day_number}</span>
                </div>

                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 overflow-hidden">
                  <div className="bg-gradient-to-r from-ocean to-ocean-light px-5 py-3 flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-semibold text-sm">{day.title}</h3>
                      <div className="flex items-center gap-3 mt-0.5">
                        {day.date && <span className="text-white/60 text-xs">{formatDate(day.date)}</span>}
                        {day.city && <span className="text-gold text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {day.city}</span>}
                      </div>
                    </div>
                    {day.day_expense_total > 0 && (
                      <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-xs font-bold">
                        ${day.day_expense_total.toFixed(0)}
                      </span>
                    )}
                  </div>

                  <div className="p-5">
                    {/* Story Note */}
                    {day.story_note && (
                      <div className="mb-4 bg-gold/5 border-l-4 border-gold/40 rounded-r-xl px-4 py-3">
                        <p className="text-charcoal/80 text-sm italic leading-relaxed">{day.story_note}</p>
                      </div>
                    )}

                    {day.photos.length > 0 && (
                      <div className="mb-4">
                        <div className={`grid gap-2 ${day.photos.length === 1 ? 'grid-cols-1' : day.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                          {day.photos.slice(0, 3).map((photo) => (
                            <div key={photo.id} className="relative rounded-xl overflow-hidden h-32 sm:h-40">
                              <img
                                src={`${API_URL}/api/journal/file/${encodeURIComponent(photo.storage_path)}?shared_timeline=${shareId}`}
                                alt={photo.caption || 'Travel photo'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              {photo.caption && (
                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                  <p className="text-white text-xs truncate">{photo.caption}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {day.activities.length > 0 && (
                      <div className="space-y-2">
                        {day.activities.map((act, i) => (
                          <div key={`act-${day.day_number}-${act.name}-${act.time || i}`} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {act.time && <span className="text-ocean/50 text-xs font-mono flex-shrink-0">{act.time}</span>}
                                <p className="text-charcoal font-medium text-sm truncate">{act.name}</p>
                              </div>
                              {act.description && <p className="text-charcoal/50 text-xs mt-0.5 line-clamp-2">{act.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Expenses */}
                    {day.expenses && day.expenses.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-charcoal/10">
                        <div className="flex flex-wrap gap-2">
                          {day.expenses.map((exp, i) => (
                            <span key={`exp-${day.day_number}-${exp.category}-${exp.amount}-${i}`} className="bg-gold/10 text-gold-dark px-2.5 py-1 rounded-full text-xs">
                              {exp.category}: {exp.currency} {exp.amount.toFixed(0)}{exp.description ? ` - ${exp.description}` : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            {review && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative pl-16 sm:pl-20 mb-10">
                <div className="absolute left-3 sm:left-5 top-0 w-7 h-7 rounded-full bg-gold border-4 border-ocean flex items-center justify-center z-10">
                  <Star className="w-3 h-3 text-ocean" />
                </div>
                <div className="bg-gold/20 backdrop-blur-xl rounded-2xl p-5 border border-gold/30">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={`star-${i}`} className={`w-4 h-4 ${i < review.rating ? 'text-gold fill-gold' : 'text-white/20'}`} />
                    ))}
                  </div>
                  <p className="text-white/80 text-sm italic">"{review.text}"</p>
                </div>
              </motion.div>
            )}

            <div className="relative pl-16 sm:pl-20">
              <div className="absolute left-3 sm:left-5 top-0 w-7 h-7 rounded-full bg-white/20 border-4 border-ocean flex items-center justify-center z-10">
                <Check className="w-3 h-3 text-white" />
              </div>
              <p className="text-white/40 text-sm pt-1">End of trip</p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-white/30 text-sm">Powered by Triple A Travel Solutions</p>
          </div>
        </div>
      </main>
    </div>
  );
}
